"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { SaveSessionWindow, SelectedDayOut } from "@/app/_components/companion/DayOutSelection";
import { dayOutFromLocal } from "@/lib/companion/day-out";
import { addDaysToIsoDate } from "@/lib/public-events/time";
import { ChevronDown } from "lucide-react";
import { resolveSpotLocalContext } from "@/lib/conditions/spot-locality";
import { remainingForecastHours } from "@/lib/conditions/forecast-presentation";
import {
  loadPlannerPreferences,
  savePlannerPreferences,
} from "@/app/actions/planner-preferences";
import {
  setShallowSearchParams,
  useShallowSearchParam,
} from "@/lib/hooks/use-shallow-search-param";
import { prefScoringKey } from "@/lib/session-planner/preferences";
import {
  plannerPreferenceStorageKey,
  plannerPreferencesForAccess,
  plannerSportFromContextKey,
  plannerSportUrlKey,
} from "@/lib/session-planner/account-preferences";
import {
  buildPlan,
  buildSupProfilePlan,
  sportLabel,
} from "@/lib/session-planner/plan";
import {
  buildHoursForSport,
  safetyContextForSport,
  type SupProfileSessionHour,
  type SessionData,
} from "@/lib/session-planner/api-types";
import { pickNearbySuggestion } from "@/lib/session-planner/nearby";
import type {
  PlannerSport,
  SessionHour,
  SessionPreferences,
} from "@/lib/session-planner/types";
import { SessionPreferencesPanel } from "./SessionPreferencesPanel";
import { SessionTimeline } from "./SessionTimeline";
import {
  clearPlannerFinderState,
  publishPlannerFinderState,
} from "@/app/_components/conditions/area/planner-finder-bridge";
import {
  isSupProfileKey,
  type SupProfileKey,
} from "@/lib/conditions/sport-profiles";

const pad = (h: number) => `${String(h).padStart(2, "0")}:00`;

const NO_SUP_PROFILES: readonly SupProfileKey[] = [];

function loadPrefs(
  sport: PlannerSport,
  availableSupProfiles: readonly SupProfileKey[],
  contextKey: string,
): SessionPreferences {
  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem(plannerPreferenceStorageKey(sport));
      if (raw) {
        return plannerPreferencesForAccess(
          sport,
          { values: JSON.parse(raw) },
          availableSupProfiles,
          contextKey,
        );
      }
    } catch {
      // ignore malformed storage
    }
  }
  return plannerPreferencesForAccess(
    sport,
    {},
    availableSupProfiles,
    contextKey,
  );
}

// The cross-spot session finder ("Find a session within reach") lives in the spot page's
// "Better nearby" block (app/_components/conditions/area/BetterNearbyClient.tsx). This card plans
// the session at this spot and publishes its sport and preferences to that finder through
// planner-finder-bridge, so both always agree.
type Props = {
  slug: string;
  /** Sport to plan when no ?sport= is set (coastal default "surf"; sailing route "sailing"). */
  defaultSport?: string;
  timezone?: string | null;
};

export function SessionPlannerCard({ slug, defaultSport = "surf", timezone }: Props) {
  const [data, setData] = useState<SessionData | null>(null);
  const [fetchState, setFetchState] = useState<"loading" | "ready" | "absent">("loading");
  const [expanded, setExpanded] = useState(false);
  const [clockTime, setClockTime] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setClockTime(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const [prefsBySport, setPrefsBySport] = useState<Record<string, SessionPreferences>>({});
  const [accountHydrationBySport, setAccountHydrationBySport] = useState<Record<string, boolean>>(
    {},
  );
  const sessionDirtySportsRef = useRef<Record<string, boolean>>({});

  const sportParam = useShallowSearchParam("sport");
  const plannerParam = useShallowSearchParam("planner");

  // Deep-link seam (The Call's "Plan it" tap, or any link): ?planner=1
  // auto-expands the card; #session-planner anchors to it. The shallow param
  // applies post-hydration, after the browser's native anchor scroll has
  // already missed, so scroll manually once the card has data.
  useEffect(() => {
    if (plannerParam === "1" && fetchState === "ready") {
      queueMicrotask(() => {
        setExpanded(true);
        document
          .getElementById("session-planner")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [plannerParam, fetchState]);

  // Defer the data fetch to idle so the planner never competes with the page's
  // own work, and the ISR shell stays light and cookie-free.
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      fetch(`/api/conditions/${encodeURIComponent(slug)}/session`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d: SessionData | null) => {
          if (cancelled) return;
          if (d && d.availableSports.length > 0 && d.hours.length > 0) {
            setData(d);
            setFetchState("ready");
          } else {
            setFetchState("absent");
          }
        })
        .catch(() => {
          if (!cancelled) setFetchState("absent");
        });
    };
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    const id = w.requestIdleCallback ? w.requestIdleCallback(run) : window.setTimeout(run, 600);
    return () => {
      cancelled = true;
      const cancelIdle = (window as Window & { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback;
      if (cancelIdle) cancelIdle(id);
      else clearTimeout(id);
    };
  }, [slug]);

  const urlSport = sportParam ?? defaultSport;
  const available = data?.availableSports ?? [];
  const availableSupProfiles = data?.availableSupProfiles ?? NO_SUP_PROFILES;
  const requested = plannerSportFromContextKey(urlSport);
  const activeSport: PlannerSport | null =
    requested && available.includes(requested) ? requested : (available[0] ?? null);

  useEffect(() => {
    if (!activeSport || accountHydrationBySport[activeSport]) return;

    let cancelled = false;
    void loadPlannerPreferences(activeSport).then((result) => {
      if (cancelled) return;
      setAccountHydrationBySport((current) => ({ ...current, [activeSport]: true }));
      if (!result.success || !result.preferences) return;
      if (sessionDirtySportsRef.current[activeSport]) return;
      const accountPreferences = plannerPreferencesForAccess(
        activeSport,
        result.preferences,
        availableSupProfiles,
        urlSport,
      );

      setPrefsBySport((current) => ({ ...current, [activeSport]: accountPreferences }));
      try {
        window.localStorage.setItem(
          plannerPreferenceStorageKey(activeSport),
          JSON.stringify(accountPreferences.values),
        );
      } catch {
        // ignore storage failures (private mode, quota)
      }
    });

    return () => {
      cancelled = true;
    };
  }, [accountHydrationBySport, activeSport, availableSupProfiles, urlSport]);

  // Derived: user changes live in prefsBySport; otherwise fall back to stored /
  // default preferences for the active sport (memoised to keep a stable identity).
  const prefs = useMemo(
    () => {
      if (!activeSport) return null;
      const stored = prefsBySport[activeSport] ?? loadPrefs(
        activeSport,
        availableSupProfiles,
        urlSport,
      );
      return plannerPreferencesForAccess(
        activeSport,
        stored,
        availableSupProfiles,
        urlSport,
      );
    },
    [activeSport, availableSupProfiles, prefsBySport, urlSport],
  );

  const onPrefsChange = (next: SessionPreferences) => {
    sessionDirtySportsRef.current[next.sport] = true;
    setPrefsBySport((p) => ({ ...p, [next.sport]: next }));
    try {
      window.localStorage.setItem(
        plannerPreferenceStorageKey(next.sport),
        JSON.stringify(next.values),
      );
    } catch {
      // ignore storage failures (private mode, quota)
    }
    void savePlannerPreferences(next.sport, next.values);
    const nextScoringKey = prefScoringKey(next);
    if (
      next.sport === "sup" &&
      isSupProfileKey(nextScoringKey) &&
      availableSupProfiles.includes(nextScoringKey)
    ) {
      setShallowSearchParams({ sport: nextScoringKey });
    }
  };

  // Hand the finder in the "Better nearby" block the same sport, preferences and URL key this
  // card plans with (it used to render the finder itself). Sailing routes have no finder.
  const finderReady = fetchState === "ready" && data !== null && data.locationType !== "sailing";
  useEffect(() => {
    if (!finderReady || !activeSport || !prefs) {
      clearPlannerFinderState(slug);
      return;
    }
    const finderScoringKey = prefScoringKey(prefs);
    const finderSupProfile = isSupProfileKey(finderScoringKey) && availableSupProfiles.includes(finderScoringKey)
      ? finderScoringKey
      : undefined;
    publishPlannerFinderState({
      slug,
      sport: activeSport,
      prefs,
      sportUrlKey: plannerSportUrlKey(activeSport, finderSupProfile),
    });
  }, [finderReady, activeSport, prefs, availableSupProfiles, slug]);
  useEffect(() => () => clearPlannerFinderState(slug), [slug]);

  const planResult = useMemo(() => {
    if (!data || !activeSport || !prefs) return null;
    const now = new Date(clockTime);
    const local = resolveSpotLocalContext(now, { timezone, longitude: data.lng });
    if (!local || local.source !== "iana" || local.date !== data.generatedDate) return null;
    const minute = Number(new Intl.DateTimeFormat("en-GB", { timeZone: local.timeZone, minute: "2-digit" }).format(now));
    const localHour = local.hour + minute / 60;
    const scoringKey = prefScoringKey(prefs);
    const safety = safetyContextForSport(data, scoringKey, activeSport);
    const ctx = {
      waterStatus: data.waterStatus,
      beachFacingDeg: data.beachFacingDeg,
      ...safety,
    };
    const nativeWindows = data.nativeWindowsBySport?.[scoringKey]?.filter((window) => window.dayOffset > 0 || window.startHour >= Math.ceil(localHour));
    const namedSupProfile = isSupProfileKey(scoringKey) &&
      availableSupProfiles.includes(scoringKey);
    const hours: SessionHour[] | SupProfileSessionHour[] = namedSupProfile
      ? remainingForecastHours(buildHoursForSport(data, scoringKey) as SupProfileSessionHour[], localHour)
      : remainingForecastHours(buildHoursForSport(data, scoringKey) as SessionHour[], localHour);
    const build = (nearby?: ReturnType<typeof pickNearbySuggestion> | null) =>
      namedSupProfile
        ? buildSupProfilePlan({
            prefs,
            hours: hours as SupProfileSessionHour[],
            ctx,
            waterTempC: data.waterTempC,
            spotName: data.name,
            nearby,
            nativeWindows,
          })
        : buildPlan({
            prefs,
            hours: hours as SessionHour[],
            ctx,
            waterTempC: data.waterTempC,
            spotName: data.name,
            nearby,
            nativeWindows,
          });
    const base = build();
    if (base.hasGoodWindows) return { plan: base, hours };
    const nearby = pickNearbySuggestion(
      data.nearby,
      scoringKey,
      base.bestWindow?.avgScore ?? 0,
      sportLabel(activeSport)
    );
    const plan = nearby ? build(nearby) : base;
    return { plan, hours };
  }, [data, activeSport, availableSupProfiles, prefs, timezone, clockTime]);

  const plan = planResult?.plan ?? null;
  const hours = planResult?.hours ?? null;

  if (fetchState !== "ready" || !data || !activeSport || !prefs || !plan || !hours) {
    if (plannerParam !== "1") return null;
    return <section id="session-planner" className="py-6"><SelectedDayOut planner /><p role="status" className="rounded-md border border-border-light bg-white p-4 text-sm text-navy">{fetchState === "loading" ? "Loading current session windows..." : "Session timing is unavailable for this place right now. Review the conditions above or choose another place."}</p></section>;
  }

  const scoringKey = prefScoringKey(prefs);
  const exactSupProfile = isSupProfileKey(scoringKey) &&
    availableSupProfiles.includes(scoringKey)
    ? scoringKey
    : undefined;
  const sportUrlKey = plannerSportUrlKey(activeSport, exactSupProfile);
  const selectedWindow = plan.bestWindow && timezone ? dayOutFromLocal({ slug, sport: sportUrlKey, timezone,
    date: addDaysToIsoDate(data.generatedDate, plan.bestWindow.dayOffset),
    startTime: pad(plan.bestWindow.peakStartHour), endTime: pad(plan.bestWindow.peakEndHour) }) : null;
  const safety = safetyContextForSport(data, scoringKey, activeSport);
  const ctx = {
    waterStatus: data.waterStatus,
    beachFacingDeg: data.beachFacingDeg,
    ...safety,
  };
  const spot = { lat: data.lat, lng: data.lng, name: data.name };
  const nearbyLoc = plan.nearbySuggestion
    ? (() => {
        const match = data.nearby.find((n) => n.slug === plan.nearbySuggestion!.slug);
        return match ? { lat: match.lat, lng: match.lng, name: match.name } : null;
      })()
    : null;
  const teaser = plan.bestWindow
    ? `Next window ${plan.bestWindow.dayLabel.toLowerCase()}, ${pad(plan.bestWindow.peakStartHour)} to ${pad(plan.bestWindow.peakEndHour)}`
    : "No standout window in the next two days";

  return (
    <section
      id="session-planner"
      className="py-6 scroll-mt-56"
    >
      <div className="overflow-hidden rounded-[18px] border border-border-light bg-white shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between gap-3 p-5 text-left md:p-6"
        >
          <div className="min-w-0">
            <h2 className="font-heading text-[20px] font-bold text-navy md:text-[24px]">
              Plan my session
            </h2>
            <p className="mt-0.5 text-[13px] text-text-muted">{teaser}</p>
          </div>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border-light text-navy">
            <ChevronDown
              size={18}
              className={`transition-transform ${expanded ? "rotate-180" : ""}`}
              aria-hidden
            />
          </span>
        </button>

        {expanded ? (
          <div className="border-t border-border-light p-5 md:p-6">
            <SelectedDayOut planner />
            <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
              <div className="order-2 lg:order-1"><SessionPreferencesPanel
                prefs={prefs}
                onChange={onPrefsChange}
                availableSupProfiles={availableSupProfiles}
              /></div>
              <div className="order-1 lg:order-2"><SessionTimeline
                plan={plan}
                prefs={prefs}
                ctx={ctx}
                hours={hours}
                sportUrlKey={sportUrlKey}
                spot={spot}
                nearbyLoc={nearbyLoc}
                windowActions={selectedWindow ? <SaveSessionWindow selection={selectedWindow} /> : null}
              /></div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
